// Search-as-you-type from Algolia, in place of Shopify's predictive search. It reuses
// PredictiveSearch (main-search.js) for the field, the panel and the popular searches, and renders
// the same results markup as sections/predictive-search.liquid, so the styles are shared.
//
// It talks to Algolia's search API directly with the public search-only key: one request per
// search for products and collections, cached per query, with stale requests cancelled. If
// Algolia fails, it falls back to Shopify's predictive search. Clicks on results are sent to
// Algolia Insights, so its analytics and relevance learning keep working, but only when the
// visitor has allowed analytics.
class AlgoliaSearch extends PredictiveSearch {
  connectedCallback() {
    this.config = algoliaConfig(this);
    algoliaWhenConsent(() => algoliaPixelConfig(this.config));
    super.connectedCallback();
    // Share cached results with the other Algolia searches on the page (header and drawer).
    this.allPredictiveSearchInstances = document.querySelectorAll('algolia-search');

    this.input.addEventListener('keydown', this.onInputKeydown.bind(this));
    this.searchResultsWrapper.addEventListener('keydown', this.onResultsKeydown.bind(this));
    this.searchResultsWrapper.addEventListener('click', this.onResultClick.bind(this));

    // On touch screens, scrolling the results puts the keyboard away so they get the screen.
    this.searchContainer = this.querySelector('.search-container');
    this.searchContainer.tabIndex = -1;
    this.searchContainer.addEventListener('touchmove', this.onResultsTouchmove.bind(this), {passive: true});
    window.addEventListener('resize', debounce(this.onResize.bind(this), 100));
  }

  // Moves focus from the field to the panel, which closes the keyboard but keeps the panel open
  // (it closes when focus leaves the search).
  onResultsTouchmove() {
    if (document.activeElement === this.input) this.searchContainer.focus({preventScroll: true});
  }

  // The results' height is fitted to the window, which changes as the keyboard opens and closes.
  onResize() {
    if (!this.hasAttribute('open')) return;
    this.resultsMaxHeight = false;
    this.showResults();
  }

  getSearchResults() {
    const term = this.searchTerm;
    if (term.length < this.minLength()) return this.clearResults();
    const key = term.toLowerCase();
    this.setLiveRegionLoading();

    // Cached Algolia results, or the markup of Shopify's results if a fallback cached them.
    const cached = this.cachedResults[key];
    if (typeof cached === 'string') return this.renderSearchResults(cached);
    if (cached) return this.renderSearchResults(this.renderResults(term, cached));

    this.abortController.abort();
    this.abortController = new AbortController();

    this.search(term, this.abortController.signal)
      .then((results) => {
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[key] = results;
        });
        // The term may have changed while the request was out.
        if (term === this.searchTerm) this.renderSearchResults(this.renderResults(term, results));
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        // Algolia is unavailable: use Shopify's predictive search instead.
        PredictiveSearch.prototype.getSearchResults.call(this);
      });
  }

  search(term, signal) {
    const {productsIndex, collectionsIndex, productLimit, collectionLimit, maxPrice} = this.config;
    return algoliaQuery(
      this.config,
      [
        {
          indexName: productsIndex,
          params: {
            query: term,
            hitsPerPage: productLimit,
            distinct: true,
            numericFilters: [`price<=${maxPrice}`],
            attributesToRetrieve: ALGOLIA_PRODUCT_ATTRIBUTES,
            attributesToHighlight: [],
            clickAnalytics: true,
            analyticsTags: ['header-search'],
          },
        },
        {
          indexName: collectionsIndex,
          params: {
            query: term,
            hitsPerPage: collectionLimit,
            attributesToRetrieve: ['handle', 'title', 'image', 'products_count'],
            attributesToHighlight: [],
            analytics: false,
          },
        },
      ],
      signal,
    ).then(([products, collections]) => ({products, collections}));
  }

  renderResults(term, {products, collections}) {
    const {strings} = this.config;
    const productHits = products.hits;
    const collectionHits = collections.hits;
    const total = productHits.length + collectionHits.length;

    if (total === 0) {
      return `<div id="Predictive-Search-Results" class="predictive-search-results">
          <div><div class="alert-message alert-error" role="alert">${escapeHtml(strings.noResults.replace('[terms]', term))}</div></div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>${escapeHtml(strings.noResults.replace('[terms]', term))}</span>`;
    }

    const tab = (name, count, selected) => `
      <li id="Search-Result-${name}-Tab" class="tab focus-inset" role="tab" tabindex="0"
          aria-controls="Search-Result-${name}-Panel" aria-selected="${selected}">
        ${escapeHtml(strings[name.toLowerCase()])}
        <span aria-hidden="true">${count}</span>
        <span class="visually-hidden">${escapeHtml(this.plural('resultsWithCount', count))}</span>
      </li>`;
    const panel = (name, cards, hidden) => `
      <div id="Search-Result-${name}-Panel" class="panel" role="tabpanel"${hidden ? ' aria-hidden="true"' : ''}
           aria-labelledby="Search-Result-${name}-Tab">${cards}</div>`;

    const productCards = productHits
      .map((hit, i) => this.productCard(hit, i + 1, products.queryID, products.index))
      .join('');
    const collectionCards = collectionHits.map((hit) => this.collectionCard(hit)).join('');

    const tabs = [
      productHits.length ? tab('Products', products.nbHits, true) : '',
      collectionHits.length ? tab('Collections', collectionHits.length, !productHits.length) : '',
    ].join('');
    const panels = [
      productHits.length ? panel('Products', productCards, false) : '',
      collectionHits.length ? panel('Collections', collectionCards, productHits.length > 0) : '',
    ].join('');

    const seeAll = products.nbHits
      ? `<div class="suggestion-searches"><div class="suggestion-list">
           <a href="${this.searchUrl(term)}" class="link link-text show-underline">${escapeHtml(this.plural('seeAll', products.nbHits))}</a>
         </div></div>`
      : '';

    return `<div id="Predictive-Search-Results" class="predictive-search-results">
        <tab-panel class="tab-panel">
          <scrollable-content-viewer class="scrollable-content" data-draggable>
            <ul class="tab-panel-tabs scrollable-content-wrapper" role="tablist">${tabs}</ul>
          </scrollable-content-viewer>
          <div class="tab-panel-panels hidden-scrollbar">${panels}</div>
        </tab-panel>
        ${seeAll}
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${escapeHtml(this.plural('resultsWithCount', products.nbHits + collectionHits.length))}</span>`;
  }

  productCard(hit, position, queryID, index) {
    const brand = algoliaBrand(hit);
    const id = `Product-Title-algolia-${hit.id}`;

    return `<div class="card-wrapper product-card-wrapper">
        <product-card class="card card-layout--horizontal">
          <div class="card-inner ratio image-hover-effect--fade" style="--ratio-percent: 100%;">
            <div class="card-media-wrapper" style="--alpha-background: 10%;">
              <div class="card-media media media-fit--cover">${this.image(hit.product_image || hit.image, hit.title)}</div>
            </div>
          </div>
          <div class="card-content text-align--left">
            <div class="card-others"></div>
            ${brand ? `<span class="product-vendor">${escapeHtml(brand)}</span>` : ''}
            <h3 class="card-heading font-body">
              <a id="${id}" class="link link-underline" href="/products/${encodeURIComponent(hit.handle)}" aria-labelledby="${id}"
                 data-algolia-object-id="${escapeHtml(hit.objectID)}" data-algolia-product-id="${escapeHtml(hit.id)}" data-algolia-position="${position}"
                 data-algolia-query-id="${escapeHtml(queryID || '')}" data-algolia-index="${escapeHtml(index)}">${escapeHtml(hit.title)}</a>
            </h3>
            <div class="card-price">${algoliaPriceHtml(hit, this.config)}</div>
          </div>
        </product-card>
      </div>`;
  }

  collectionCard(hit) {
    const {strings} = this.config;
    const id = `CardLink-algolia-${escapeHtml(hit.objectID)}`;
    const href = `/collections/${encodeURIComponent(hit.handle)}`;
    return `<div class="card-wrapper collection-card-wrapper hover-trigger">
        <div class="card card-layout--horizontal">
          <div class="card-inner ratio overlay-block" style="--ratio-percent: 100%;">
            <div class="card-media-wrapper">${
              hit.image
                ? `<div class="card-media media">${this.image(hit.image, hit.title)}</div>`
                : `<div class="card-media media collection-preview-grid" data-collection-preview="${escapeHtml(hit.handle)}"></div>`
            }</div>
          </div>
          <div class="card-content">
            <h3 class="card-heading font-body"><a id="${id}" class="link link-underline" href="${href}">${escapeHtml(hit.title)}</a></h3>
            <div class="card-buttons">
              <a class="button button--mini button--pill button--hover-animate" href="${href}" tabindex="-1">${escapeHtml(strings.viewAll)}</a>
            </div>
            <small class="light">${escapeHtml(this.plural('productCount', hit.products_count || 0))}</small>
          </div>
        </div>
      </div>`;
  }

  renderSearchResults(html) {
    super.renderSearchResults(html);
    this.loadCollectionPreviews();
  }

  // A collection without an image shows its first four products' images in a 2x2 grid. They're
  // fetched (from sections/collection-preview-images.liquid) when the grid first comes into view,
  // so only when someone opens the Collections tab.
  loadCollectionPreviews() {
    const grids = this.searchResultsWrapper.querySelectorAll('[data-collection-preview]');
    if (!grids.length) return;
    this.previewObserver?.disconnect();
    this.previewObserver = new IntersectionObserver((entries) => {
      entries.forEach(({isIntersecting, target}) => {
        if (!isIntersecting) return;
        this.previewObserver.unobserve(target);
        collectionPreviewImages(target.dataset.collectionPreview).then((images) => {
          target.innerHTML = images
            .map(({src, alt}) => `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" width="80" height="80" loading="lazy">`)
            .join('');
        });
      });
    });
    grids.forEach((grid) => this.previewObserver.observe(grid));
  }

  image(src, alt) {
    return shopifyImage(src, alt, [165, 360], '(min-width: 750px) 300px, 200px');
  }

  plural(name, count) {
    return algoliaPlural(this.config, name, count);
  }

  searchUrl(term) {
    return `${this.config.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
  }

  // Arrow down from the field moves into the visible results; arrows then move between them.
  onInputKeydown(event) {
    if (event.key === 'ArrowDown' && this.resultLinks().length) {
      event.preventDefault();
      this.resultLinks()[0].focus();
    } else if (event.key === 'Escape') {
      this.input.blur();
      this.onFocusout();
    }
  }

  onResultsKeydown(event) {
    const links = this.resultLinks();
    const index = links.indexOf(document.activeElement);
    if (index < 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = index + (event.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) this.input.focus();
      else links[Math.min(next, links.length - 1)].focus();
    } else if (event.key === 'Escape') {
      this.input.focus();
    }
  }

  resultLinks() {
    return [...this.searchResultsWrapper.querySelectorAll('.panel:not([aria-hidden]) .card-heading a, .suggestion-list a')];
  }

  onResultClick(event) {
    algoliaClick(this.config, event, 'Header search result clicked');
  }
}

// Shared with the search page (algolia-search-results.js).

// The element's JSON config. Its strings come from Liquid's t filter, which HTML-escapes them, so
// they're decoded here and escaped again where they're output.
function algoliaConfig(element) {
  const config = JSON.parse(element.querySelector('script[data-algolia-config]').textContent);
  const decoder = document.createElement('textarea');
  Object.keys(config.strings || {}).forEach((key) => {
    decoder.innerHTML = config.strings[key];
    config.strings[key] = decoder.value;
  });
  return config;
}

const ALGOLIA_PRODUCT_ATTRIBUTES = ['id', 'handle', 'title', 'image', 'product_image', 'price', 'compare_at_price', 'variants_min_price', 'variants_max_price', 'tags', 'inventory_available', 'meta.custom.brand'];

// Runs several searches in one request and returns their results in order. Credentials in the
// query string and a form content type keep it a "simple" request, so the browser doesn't send a
// CORS preflight first. Falls back to Algolia's second host if the first fails.
function algoliaQuery({appId, apiKey}, requests, signal) {
  const params = (values) =>
    Object.entries(values)
      .filter(([, value]) => value !== undefined)
      .map(([name, value]) => `${name}=${encodeURIComponent(typeof value === 'string' ? value : JSON.stringify(value))}`)
      .join('&');
  const body = JSON.stringify({requests: requests.map(({indexName, params: values}) => ({indexName, params: params(values)}))});
  const auth = `x-algolia-application-id=${appId}&x-algolia-api-key=${apiKey}`;
  const request = (host) =>
    fetch(`https://${host}/1/indexes/*/queries?${auth}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body,
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error(`Algolia ${response.status}`);
      return response.json();
    });

  return request(`${appId}-dsn.algolia.net`)
    .catch((error) => {
      if (error?.name === 'AbortError') throw error;
      return request(`${appId}-1.algolianet.com`);
    })
    .then(({results}) => results);
}

// Tells Algolia Insights which result was clicked, if the visitor allows analytics. The link
// carries the result's object ID (the variant), product ID, position, query ID and index as data
// attributes.
function algoliaClick(config, event, eventName) {
  const link = event.target.closest('[data-algolia-object-id]');
  if (!link || !link.dataset.algoliaQueryId || !analyticsAllowed()) return;
  algoliaPixelAttribution(config, link.dataset);
  algoliaSendEvent(config, {
    eventType: 'click',
    eventName,
    index: link.dataset.algoliaIndex,
    queryID: link.dataset.algoliaQueryId,
    objectIDs: [link.dataset.algoliaObjectId],
    positions: [Number(link.dataset.algoliaPosition)],
  });
}

// Sends one event to Algolia Insights with this visitor's token. Callers check consent first.
function algoliaSendEvent({appId, apiKey}, event) {
  fetch('https://insights.algolia.io/1/events', {
    method: 'POST',
    keepalive: true,
    headers: {'Content-Type': 'application/json', 'X-Algolia-Application-Id': appId, 'X-Algolia-API-Key': apiKey},
    body: JSON.stringify({events: [{...event, userToken: algoliaUserToken()}]}),
  }).catch(() => {});
}

// The brand the cards show: the product's "brand:" tag.
function algoliaBrand(hit) {
  return (
    hit.meta?.custom?.brand?.fields?.label ||
    (hit.tags || []).find((tag) => tag.startsWith('brand:'))?.slice(6) ||
    ''
  );
}

// The product's own badges (custom.badges metaobjects), as snippets/product-badges.liquid renders
// them: on every card, whatever their display field says. The search page's cards show them; the
// header's are too small.
function algoliaBadgesHtml(hit) {
  const badges = (hit.meta?.custom?.badges || []).map((badge) => badge.fields || {}).filter((badge) => badge.label);
  if (!badges.length) return '';
  const html = badges
    .map(({label, url, background_colour: background, text_colour: text, opacity}) => {
      const style = `background-color: ${escapeHtml(background || '')}; color: ${escapeHtml(text || '#fff')}; opacity: ${Number(opacity) || 1}`;
      return url
        ? `<a class="badge" href="${escapeHtml(url)}" style="${style}">${escapeHtml(label)}</a>`
        : `<span class="badge" style="${style}">${escapeHtml(label)}</span>`;
    })
    .join('');
  return `<div class="card-badges position--left">${html}</div>`;
}

// The card price, as snippets/price.liquid renders it.
function algoliaPriceHtml(hit, {strings, moneyFormat}) {
  const money = (amount) => formatMoney(amount, moneyFormat);
  const varies = hit.variants_min_price !== hit.variants_max_price;
  const price = varies ? strings.fromPrice.replace('[price]', money(hit.variants_min_price)) : money(hit.price);
  const inner =
    hit.compare_at_price > hit.price && !varies
      ? `<span class="visually-hidden">${escapeHtml(strings.salePrice)}</span>
         <b class="price-item">${price}</b>
         <span class="visually-hidden">${escapeHtml(strings.regularPrice)}</span>
         <s class="price-item was-price">was&nbsp;${money(hit.compare_at_price)}</s>`
      : `<span class="visually-hidden">${escapeHtml(strings.regularPrice)}</span>
         <b class="price-item">${price}</b>`;
  return `<div class="price"><div class="price-container">${inner}</div></div>`;
}

// A plural string from the config: "<name>One" for one, "<name>" (with the count token) otherwise.
function algoliaPlural({strings, countToken}, name, count) {
  const text = count === 1 && strings[`${name}One`] ? strings[`${name}One`] : strings[name];
  return text.replace(String(countToken), count.toLocaleString());
}

function formatMoney(amount, format = '£{{amount}}') {
  const value = Number(amount || 0);
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
    const fixed = value.toFixed(kind.includes('no_decimals') ? 0 : 2);
    const [whole, cents] = fixed.split('.');
    const comma = kind.includes('comma_separator');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
    return cents ? `${grouped}${comma ? ',' : '.'}${cents}` : grouped;
  });
}

// A Shopify CDN image at the given widths. Algolia stores the full-size URL.
function shopifyImage(src, alt, widths, sizes) {
  if (!src) return '';
  const sized = (width) => {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  };
  return `<img src="${sized(widths[0])}" srcset="${widths.map((width) => `${sized(width)} ${width}w`).join(', ')}" sizes="${sizes}"
    alt="${escapeHtml(alt || '')}" width="${widths[widths.length - 1]}" height="${widths[widths.length - 1]}" loading="lazy" class="product-card-featured-image">`;
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[char]);
}

const collectionPreviews = new Map();
function collectionPreviewImages(handle) {
  if (!collectionPreviews.has(handle)) {
    const root = window.Shopify?.routes?.root || '/';
    collectionPreviews.set(
      handle,
      fetch(`${root}collections/${encodeURIComponent(handle)}?section_id=collection-preview-images`)
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          const json = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-collection-preview-images]');
          return json ? JSON.parse(json.textContent) : [];
        })
        .catch(() => []),
    );
  }
  return collectionPreviews.get(handle);
}

function analyticsAllowed() {
  try {
    return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
  } catch {
    return false;
  }
}

// Runs the callback once Shopify's consent API has loaded (it loads after the page), and again
// whenever the visitor answers the cookie banner.
function algoliaWhenConsent(callback) {
  document.addEventListener('visitorConsentCollected', () => callback());
  if (window.Shopify?.customerPrivacy) return callback();
  window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.1'}], (error) => {
    if (!error) callback();
  });
}

// Conversions (add to cart, checkout completed) are sent by the Algolia app's web pixel, not by
// the theme. The pixel reads two localStorage keys that the app's embed used to write: its
// settings (algolia_config) and the search results clicked (algolia_analytics_clicked_objects),
// which it matches to cart items by variant or product to credit the search. See
// https://www.algolia.com/doc/integration/shopify/building-search-ui/events
//
// The settings carry this visitor's token, so conversions and clicks share it; without one the
// pixel makes up a new token for every event. Written only once the consent API has loaded, so
// an early "not allowed" doesn't drop a token the visitor allowed.
function algoliaPixelConfig({appId, apiKey, productsIndex}) {
  try {
    const config = {app_id: appId, search_api_key: apiKey, index_name: productsIndex};
    if (analyticsAllowed()) config.user_token = algoliaUserToken();
    localStorage.setItem('algolia_config', JSON.stringify(config));
  } catch {}
}

function algoliaPixelAttribution(config, {algoliaIndex, algoliaQueryId, algoliaObjectId, algoliaProductId}) {
  const key = 'algolia_analytics_clicked_objects';
  try {
    algoliaPixelConfig(config);
    const clicked = JSON.parse(localStorage.getItem(key) || '[]');
    clicked.push({
      eventName: 'Added to cart',
      index: algoliaIndex,
      queryID: algoliaQueryId,
      objectIDs: [algoliaObjectId],
      productId: algoliaProductId,
    });
    // Keep the most recent clicks only; nothing else trims the list.
    localStorage.setItem(key, JSON.stringify(clicked.slice(-50)));
  } catch {}
}

// An anonymous ID for Algolia Insights, kept in this browser.
function algoliaUserToken() {
  const key = 'ecd_algolia_user_token';
  try {
    let token = localStorage.getItem(key);
    if (!token) {
      token = `anon-${crypto.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, token);
    }
    return token;
  } catch {
    return 'anon-unknown';
  }
}

customElements.define('algolia-search', AlgoliaSearch);
